import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard, Users, UserPlus, Stethoscope, Activity,
  Calendar, Receipt, MessageSquare, BarChart3, Settings, Phone, ClipboardList, Megaphone, MessageCircle,
} from 'lucide-react';
import { useListCallLogsQuery, useMarkAllCallsSeenMutation } from '../../api/callLogsApi';
import { useListLeadsQuery } from '../../api/leadsApi';
import { useGetInboxSummaryQuery } from '../../api/whatsappApi';
import './layout.css';
import logo from '../../assets/logo.png';

const NAV = [
  { to: '/dashboard',       label: 'Dashboard',     icon: LayoutDashboard },
  { to: '/tasks',           label: "Today's Tasks", icon: ClipboardList },
  { to: '/call-logs',       label: 'Call Logs',     icon: Phone,    badge: 'callLogs' },
  { to: '/leads',           label: 'Leads',         icon: UserPlus, badge: 'leads' },
  { to: '/consultations',   label: 'Consultations', icon: Stethoscope },
  { to: '/patients',        label: 'Patients',      icon: Users },
  { to: '/appointments',    label: 'Appointments',  icon: Calendar },
  { to: '/sessions',        label: 'Sessions',      icon: Activity },
  { to: '/billing',         label: 'Billing',       icon: Receipt },
  { to: '/whatsapp',        label: 'WhatsApp',      icon: MessageCircle, badge: 'whatsapp' },
  { to: '/communications',  label: 'Communications',icon: MessageSquare },
  { to: '/campaigns',       label: 'Campaigns',     icon: Megaphone },
  { to: '/reports',         label: 'Reports',       icon: BarChart3 },
  { to: '/staff',           label: 'Staff',         icon: Users },
  { to: '/settings',        label: 'Settings',      icon: Settings },
];

export default function Sidebar({ collapsed }) {
  // Track last time the user viewed the Leads / Call Logs pages. Stored in
  // localStorage so new records arriving after that timestamp drive the
  // "unread" counter in the sidebar.
  //
  // First-time visitor seeding: when no lastSeen exists yet, immediately
  // initialise it to "now" — otherwise the badge would show the patient's
  // entire historical count (e.g. all 9 call logs ever) until they click in.
  // Seeding here means the badge starts at 0 and only goes up when a NEW
  // call log arrives after this session began.
  const [leadsLastSeen, setLeadsLastSeen] = useState(() => {
    const existing = localStorage.getItem('leads.lastSeen');
    if (existing) return existing;
    const seed = new Date().toISOString();
    try { localStorage.setItem('leads.lastSeen', seed); } catch (_) { /* private mode */ }
    return seed;
  });
  const [callsLastSeen, setCallsLastSeen] = useState(() => {
    const existing = localStorage.getItem('callLogs.lastSeen');
    if (existing) return existing;
    const seed = new Date().toISOString();
    try { localStorage.setItem('callLogs.lastSeen', seed); } catch (_) { /* private mode */ }
    return seed;
  });

  // Live record counters: count only items created since the user last viewed.
  // Polling on a 20-second cadence keeps the badge live without a WebSocket — the existing
  // /api/call-logs?createdFrom= endpoint is cheap (single COUNT). On a new call landing,
  // the next poll bumps the badge; staff don't have to refresh.
  const { data: callLogsNew } = useListCallLogsQuery(
    { unseenSince: callsLastSeen, page: 1, pageSize: 1 },
    { pollingInterval: 20000, refetchOnFocus: true, refetchOnReconnect: true },
  );
  const { data: leadsNew } = useListLeadsQuery(
    { createdFrom: leadsLastSeen, page: 1, pageSize: 1 },
    { pollingInterval: 20000, refetchOnFocus: true },
  );
  // Sidebar nav-click also fires PATCH /call-logs/mark-all-seen so the server-side
  // status flips New→Reviewed in one round trip — covers multi-device sessions.
  const [markAllCallsSeen] = useMarkAllCallsSeenMutation();

  // Unlike Leads/Call Logs, this isn't a "created since you last looked" figure — the server tracks
  // a real per-conversation unread count that resets when the thread is opened, so no localStorage
  // timestamp is involved. Polled less often since it's a badge, not the inbox itself.
  const { data: waSummary } = useGetInboxSummaryQuery(undefined, {
    pollingInterval: 30000, refetchOnFocus: true, skipPollingIfUnfocused: true,
  });

  const counts = {
    callLogs: callLogsNew?.totalCount ?? 0,
    leads: leadsNew?.totalCount ?? 0,
    whatsapp: waSummary?.unreadConversations ?? 0,
  };

  // Keep local `lastSeen` in sync with any external updates to localStorage
  // (e.g. another tab clicked into the page).
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'leads.lastSeen') setLeadsLastSeen(e.newValue);
      if (e.key === 'callLogs.lastSeen') setCallsLastSeen(e.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <aside className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-brand">
        <img src={logo} alt="Logo" className="brand-logo" height={40} />
        {!collapsed && (
          <div className="brand-text">
            <div className="brand-name">The Healing Clinic</div>
            <div className="brand-sub">Physiotherapy CRM</div>
          </div>
        )}
      </div>
      <nav className="sidebar-nav">
        {NAV.map(({ to, label, icon: Icon, badge }) => {
          const count = badge ? counts[badge] : null;
          // When clicking the Leads or Call Logs links, mark the page as seen by
          // saving the current timestamp to localStorage so the unread counter resets.
          // Call Logs additionally fires a server-side bulk reset (PATCH mark-all-seen)
          // so the unseen count clears across devices, not just this browser.
          const onNavClick = () => {
            const now = new Date().toISOString();
            try {
              if (to === '/leads') {
                localStorage.setItem('leads.lastSeen', now);
                setLeadsLastSeen(now);
              }
              if (to === '/call-logs') {
                localStorage.setItem('callLogs.lastSeen', now);
                setCallsLastSeen(now);
                // Fire-and-forget; UI doesn't block on the round-trip and the poll
                // will reconcile if the request actually fails.
                markAllCallsSeen().unwrap().catch(() => undefined);
              }
            } catch (_) { /* private mode / quota — best effort */ }
          };

          return (
            <NavLink
              key={to}
              to={to}
              onClick={onNavClick}
              className={({ isActive }) => `sb-link ${isActive ? 'active' : ''}`}
            >
              <Icon size={18} />
              {!collapsed && <span>{label}</span>}
              {!collapsed && count != null && count > 0 && (
                <span className="sb-badge">{count > 999 ? '999+' : count.toLocaleString()}</span>
              )}
            </NavLink>
          );
        })}
      </nav>
    </aside>
  );
}
